import React from 'react';
import { Dropdown } from './Dropdown';

export interface LanguageOption {
  code: string;
  label: string;
}

interface LanguageMenuProps {
  value: string;
  languages: LanguageOption[];
  onChange: (code: string) => void;
  'aria-label'?: string;
}

const GlobeIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </svg>
);

/** Header language switcher as a dropdown. Shared across apps. */
export const LanguageMenu: React.FC<LanguageMenuProps> = ({ value, languages, onChange, ...rest }) => {
  const current = languages.find((l) => value.startsWith(l.code)) ?? languages[0];
  return (
    <Dropdown
      aria-label={rest['aria-label'] ?? 'Language'}
      triggerClassName="flex items-center gap-1.5 rounded-md border border-line px-2 py-1.5 text-xs font-medium text-muted-strong hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      panelClassName="w-40"
      trigger={
        <>
          <GlobeIcon />
          <span className="uppercase">{current?.code}</span>
        </>
      }
    >
      {languages.map((l) => (
        <button
          key={l.code}
          type="button"
          role="menuitem"
          onClick={() => onChange(l.code)}
          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-canvas ${
            l.code === current?.code ? 'font-medium text-brand-ink-strong' : 'text-body-soft'
          }`}
        >
          {l.label}
          {l.code === current?.code && <span className="text-brand-ink">✓</span>}
        </button>
      ))}
    </Dropdown>
  );
};
